import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  ChevronLeft, Star, Loader2, AlertTriangle, CheckCircle2,
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import { ROUTES } from '@/config/routes'
import useAuthStore from '@/stores/authStore'
import Button from '@/components/ui/Button'

const TAGS = [
  'Punctual', 'Professional', 'Clean', 'Skilled',
  'Communicative', 'Careful', 'Fast', 'Friendly',
]

function StarRating({ value, onChange }) {
  const [hovered, setHovered] = useState(0)
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((n) => (
        <button
          key={n}
          type="button"
          onClick={() => onChange(n)}
          onMouseEnter={() => setHovered(n)}
          onMouseLeave={() => setHovered(0)}
          className="transition-transform active:scale-90"
        >
          <Star
            className={`w-9 h-9 transition-colors
              ${(hovered || value) >= n
                ? 'text-yellow-400 fill-yellow-400'
                : 'text-surface-border'}`}
          />
        </button>
      ))}
    </div>
  )
}

const STAR_LABELS = ['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent']

export default function ReviewWorker() {
  const { id: bookingId } = useParams()
  const navigate = useNavigate()
  const profile  = useAuthStore((s) => s.profile)

  const [loading,    setLoading]    = useState(true)
  const [booking,    setBooking]    = useState(null)
  const [workerName, setWorkerName] = useState(null)
  const [pageError,  setPageError]  = useState(null)
  const [alreadyReviewed, setAlreadyReviewed] = useState(false)

  const [rating,     setRating]     = useState(0)
  const [tags,       setTags]       = useState([])
  const [comment,    setComment]    = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [done,       setDone]       = useState(false)
  const [err,        setErr]        = useState(null)

  useEffect(() => {
    if (!bookingId || !profile?.id) return

    async function load() {
      setLoading(true)

      const { data: bk, error } = await supabase
        .from('bookings')
        .select('id, ref_number, status, worker_id, customer_id')
        .eq('id', bookingId)
        .eq('customer_id', profile.id)
        .single()

      if (error || !bk) {
        setPageError('Booking not found.')
        setLoading(false)
        return
      }

      if (bk.status !== 'completed') {
        setPageError('You can only review a completed job.')
        setLoading(false)
        return
      }

      setBooking(bk)

      // Get worker first name
      if (bk.worker_id) {
        const { data: wp } = await supabase
          .from('profiles')
          .select('full_name')
          .eq('id', bk.worker_id)
          .single()
        if (wp?.full_name) {
          setWorkerName(wp.full_name.split(' ')[0])
        }
      }

      // Check if already reviewed
      const { data: existing } = await supabase
        .from('reviews')
        .select('id')
        .eq('booking_id', bookingId)
        .eq('customer_id', profile.id)
        .maybeSingle()

      if (existing) setAlreadyReviewed(true)

      setLoading(false)
    }

    load()
  }, [bookingId, profile?.id]) // eslint-disable-line

  function toggleTag(tag) {
    setTags((prev) =>
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    )
  }

  async function handleSubmit() {
    if (rating === 0) { setErr('Please select a star rating.'); return }
    setErr(null)
    setSubmitting(true)

    const { error: insertErr } = await supabase.from('reviews').insert({
      booking_id:  bookingId,
      customer_id: profile.id,
      worker_id:   booking.worker_id,
      rating,
      tags,
      comment:     comment.trim() || null,
    })

    if (insertErr) {
      setErr('Failed to submit review. Please try again.')
      setSubmitting(false)
      return
    }

    // Update worker rating average (best-effort via RPC or manual)
    if (booking.worker_id) {
      const { data: allReviews } = await supabase
        .from('reviews')
        .select('rating')
        .eq('worker_id', booking.worker_id)

      if (allReviews?.length) {
        const avg = allReviews.reduce((s, r) => s + r.rating, 0) / allReviews.length
        await supabase
          .from('worker_profiles')
          .update({ rating_avg: Math.round(avg * 10) / 10, jobs_completed: allReviews.length })
          .eq('id', booking.worker_id)
      }
    }

    setDone(true)
    setSubmitting(false)
  }

  // ── Loading ─────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-dvh flex items-center justify-center bg-surface-base">
        <Loader2 className="w-6 h-6 text-siglat-teal animate-spin" />
      </div>
    )
  }

  if (pageError) {
    return (
      <div className="min-h-dvh flex flex-col items-center justify-center bg-surface-base gap-4 px-6 text-center">
        <AlertTriangle className="w-8 h-8 text-siglat-red" />
        <p className="text-ink-primary font-semibold">{pageError}</p>
        <button onClick={() => navigate(ROUTES.BOOK_HISTORY)} className="text-sm text-siglat-teal hover:underline">
          Back to My Projects
        </button>
      </div>
    )
  }

  // ── Already reviewed ─────────────────────────────────────────────────────────
  if (alreadyReviewed || done) {
    return (
      <div className="min-h-dvh bg-surface-base flex flex-col">
        <div className="bg-surface-raised border-b border-surface-border px-4 pt-12 pb-4 safe-top">
          <div className="page-container">
            <p className="text-sm text-ink-muted">Review</p>
          </div>
        </div>

        <div className="flex-1 flex flex-col items-center justify-center px-6 text-center gap-5">
          <div className="w-16 h-16 rounded-full bg-yellow-500/10 border border-yellow-500/25 flex items-center justify-center">
            <Star className="w-8 h-8 text-yellow-400 fill-yellow-400" />
          </div>
          <div>
            <p className="font-display text-xl font-bold text-ink-primary mb-2">
              {alreadyReviewed && !done ? 'Already Reviewed' : 'Review Submitted!'}
            </p>
            <p className="text-sm text-ink-muted leading-relaxed">
              {alreadyReviewed && !done
                ? 'You have already submitted a review for this job.'
                : `Thank you! Your feedback helps us improve our service.`}
            </p>
          </div>
          <Button fullWidth size="lg" onClick={() => navigate(ROUTES.BOOK_HISTORY)}>
            Back to My Projects
          </Button>
        </div>
      </div>
    )
  }

  // ── Review form ──────────────────────────────────────────────────────────────
  return (
    <div className="min-h-dvh bg-surface-base flex flex-col">

      {/* Header */}
      <div className="bg-surface-raised border-b border-surface-border px-4 pt-12 pb-4 safe-top shrink-0">
        <div className="page-container">
          <button
            onClick={() => navigate(ROUTES.BOOK_HISTORY)}
            className="flex items-center gap-1 text-sm text-ink-muted hover:text-ink-secondary mb-3 -ml-1"
          >
            <ChevronLeft className="w-4 h-4" />
            My Projects
          </button>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-yellow-500/10 border border-yellow-500/25 flex items-center justify-center shrink-0">
              <Star className="w-5 h-5 text-yellow-400" />
            </div>
            <div>
              <h1 className="font-display text-xl font-bold text-ink-primary leading-tight">
                Rate Your Worker
              </h1>
              <p className="text-xs text-ink-muted">
                {booking?.ref_number ?? bookingId.slice(0, 8).toUpperCase()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <div className="page-container py-5 flex flex-col gap-6">

          {/* Star rating */}
          <div className="flex flex-col items-center gap-3 py-4">
            <p className="text-sm font-medium text-ink-secondary">
              How was {workerName ?? 'your worker'}?
            </p>
            <StarRating value={rating} onChange={setRating} />
            {rating > 0 && (
              <p className="text-sm font-semibold text-yellow-400">{STAR_LABELS[rating]}</p>
            )}
          </div>

          {/* Tags */}
          <div>
            <p className="text-sm font-medium text-ink-secondary mb-3">
              What stood out? <span className="text-ink-muted font-normal">(optional)</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {TAGS.map((tag) => (
                <button
                  key={tag}
                  type="button"
                  onClick={() => toggleTag(tag)}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors
                    ${tags.includes(tag)
                      ? 'bg-siglat-teal/15 border-siglat-teal/40 text-siglat-teal font-medium'
                      : 'bg-surface-overlay border-surface-border text-ink-muted hover:text-ink-primary'}`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>

          {/* Comment */}
          <div>
            <label className="block text-sm font-medium text-ink-secondary mb-1.5">
              Additional Comments <span className="text-ink-muted font-normal">(optional)</span>
            </label>
            <textarea
              rows={3}
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your experience…"
              maxLength={500}
              className="w-full bg-surface-overlay border border-surface-border rounded-xl px-4 py-3 text-ink-primary placeholder:text-ink-muted text-sm focus:outline-none focus:border-siglat-teal transition-colors resize-none"
            />
            <p className="text-xs text-ink-muted mt-1 text-right">{comment.length}/500</p>
          </div>

          {err && (
            <div className="bg-siglat-red/10 border border-siglat-red/25 rounded-xl px-4 py-3">
              <p className="text-sm text-siglat-red">{err}</p>
            </div>
          )}

        </div>
      </div>

      {/* Bottom CTA */}
      <div className="shrink-0 bg-surface-raised border-t border-surface-border px-4 py-4 safe-bottom">
        <div className="page-container flex flex-col gap-2">
          <Button
            fullWidth
            size="lg"
            loading={submitting}
            disabled={rating === 0}
            onClick={handleSubmit}
          >
            <CheckCircle2 className="w-4 h-4" />
            Submit Review
          </Button>
          <button
            onClick={() => navigate(ROUTES.BOOK_HISTORY)}
            className="text-sm text-center text-ink-muted hover:text-ink-primary py-1"
          >
            Skip
          </button>
        </div>
      </div>

    </div>
  )
}
